import type { EffectData, EffectMap, Ruleset } from "./Ruleset";

export class Effect<T extends EffectData> {
  ruleset: Ruleset;
  data: T;

  constructor(ruleset: Ruleset, data: T) {
    this.ruleset = ruleset;
    this.data = data;
  }

  static from(ruleset: Ruleset, data: EffectData) {
    switch (data.type) {
      case "ADD_ACTION_MODIFIER":
      case "REMOVE_ACTION":
      case "GAIN_ACTION": {
        return new ActionEffect(ruleset, data);
      }

      case "ADD_DEFENSE_MODIFIER":
      case "REMOVE_DEFENSE": {
        return new DefenseEffect(ruleset, data);
      }

      case "ADD_MOMENTUM_LIMIT_MODIFIER": {
        return new MomentumLimitEffect(ruleset, data);
      }

      default: {
        return new SpecialRuleEffect(ruleset, data);
      }
    }
  }
}

export class MomentumLimitEffect extends Effect<
  EffectMap["ADD_MOMENTUM_LIMIT_MODIFIER"]
> {
  get type() {
    return this.data.type;
  }

  get rule() {
    return this.data.value;
  }

  get value() {
    return this.data.value;
  }
}

export class SpecialRuleEffect extends Effect<EffectMap["ADD_SPECIAL_RULE"]> {
  get type() {
    return this.data.type;
  }

  get rule() {
    return this.data.value;
  }

  get group() {
    return this.data.group;
  }
}

export class ActionEffect extends Effect<
  EffectMap["ADD_ACTION_MODIFIER" | "GAIN_ACTION" | "REMOVE_ACTION"]
> {
  get type() {
    return this.data.type;
  }

  get value() {
    return "value" in this.data ? this.data.value : 0;
  }

  get action() {
    return Array.isArray(this.data.action)
      ? this.data.action.map((ref) =>
          this.ruleset.actions.get(ref.id).valueOrThrow(),
        )
      : this.ruleset.actions.get(this.data.action.id).valueOrThrow();
  }
}

export class DefenseEffect extends Effect<
  EffectMap["ADD_DEFENSE_MODIFIER" | "REMOVE_DEFENSE"]
> {
  get type() {
    return this.data.type;
  }

  get value() {
    return "value" in this.data ? this.data.value : 0;
  }

  get defense() {
    return Array.isArray(this.data.defense)
      ? this.data.defense.map((ref) =>
          this.ruleset.defenses.get(ref.id).valueOrThrow(),
        )
      : this.ruleset.defenses.get(this.data.defense.id).valueOrThrow();
  }
}
